import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import ClientesPDFExport from '../components/ClientesPDFExport';
import { useAuthStore } from '../store/authStore';
import { showConfirm } from '../lib/swal';
import { validarRut, formatearRut, limpiarRut } from '../utils/rutUtils';
import toast, { Toaster } from 'react-hot-toast';

export default function Clientes() {
  const { user } = useAuthStore();
  const [clientes, setClientes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ nombre: '', rut: '', ubicacion: '', dia_facturacion: '', toma_contador: '' });
  const [editId, setEditId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [busqueda, setBusqueda] = useState('');
  const itemsPerPage = 10;

  useEffect(() => {
    fetchClientes();
  }, []);

  const fetchClientes = async () => {
    const { data, error } = await supabase
      .from('clientes')
      .select('*')
      .order('dia_facturacion', { ascending: true, nullsLast: true });
    if (!error) setClientes(data);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'rut') {
      // Formatear el RUT automáticamente mientras se escribe (12.345.678-9)
      setFormData({ ...formData, rut: formatearRut(value) });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const openModal = (cliente = null) => {
    if (cliente) {
      setFormData({
        nombre: cliente.nombre,
        rut: cliente.rut,
        ubicacion: cliente.ubicacion,
        dia_facturacion: cliente.dia_facturacion || '',
        toma_contador: cliente.toma_contador || '',
      });
      setEditId(cliente.id);
    } else {
      setFormData({ nombre: '', rut: '', ubicacion: '', dia_facturacion: '', toma_contador: '' });
      setEditId(null);
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar el RUT (dígito verificador chileno)
    if (!validarRut(formData.rut)) {
      toast.error('El RUT ingresado no es válido. Revisa el número y el dígito verificador.', { duration: 5000 });
      return;
    }

    // Verificar que no exista otro cliente con el mismo RUT
    const rutNormalizado = limpiarRut(formData.rut);
    const duplicado = clientes.find(
      c => limpiarRut(c.rut) === rutNormalizado && c.id !== editId
    );
    if (duplicado) {
      toast.error(`Ya existe un cliente con ese RUT: ${duplicado.nombre}.`, { duration: 5000 });
      return;
    }

    // Guardar el RUT siempre formateado, para mantener consistencia
    const datosGuardar = { ...formData, rut: formatearRut(formData.rut) };

    if (editId) {
      await supabase.from('clientes').update(datosGuardar).eq('id', editId);
    } else {
      await supabase.from('clientes').insert([datosGuardar]);
    }
    setShowModal(false);
    fetchClientes();
  };

  const handleDelete = async (id) => {
    const confirmado = await showConfirm(
      '¿Eliminar este cliente?',
      'Esta acción no se puede deshacer.',
      { peligro: true }
    );
    if (confirmado) {
      await supabase.from('clientes').delete().eq('id', id);
      fetchClientes();
    }
  };

  // Filtrado por búsqueda (nombre, RUT o ubicación)
  const terminoBusqueda = busqueda.trim().toLowerCase();
  const clientesFiltrados = terminoBusqueda
    ? clientes.filter((c) => {
        const nombre = (c.nombre || '').toLowerCase();
        const rut = (c.rut || '').toLowerCase();
        const ubicacion = (c.ubicacion || '').toLowerCase();
        return (
          nombre.includes(terminoBusqueda) ||
          rut.includes(terminoBusqueda) ||
          ubicacion.includes(terminoBusqueda)
        );
      })
    : clientes;

  // Pagination logic (sobre la lista filtrada)
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = clientesFiltrados.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(clientesFiltrados.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevious = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNext = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  const handleBusquedaChange = (e) => {
    setBusqueda(e.target.value);
    setCurrentPage(1); // Volver a la primera página al buscar
  };

  return (
    <div className="ml-0 lg:ml-72 p-4 sm:p-6 lg:p-8 pt-16 lg:pt-6 min-h-screen animate-fadeIn">
      <Toaster position="top-right" />
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
              <span className="text-gradient">Gestión de Clientes</span>
            </h1>
            <p className="text-gray-600 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              {terminoBusqueda
                ? `${clientesFiltrados.length} de ${clientes.length} ${clientes.length === 1 ? 'cliente' : 'clientes'} (filtrados)`
                : `${clientes.length} ${clientes.length === 1 ? 'cliente registrado' : 'clientes registrados'}`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <ClientesPDFExport clientes={clientesFiltrados} />
            {user?.role === 'admin' && (
              <button
                onClick={() => openModal()}
                className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-medium rounded-xl hover:shadow-lg transition-all duration-200 transform hover:scale-105 active:scale-95"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Nuevo Cliente
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Buscador */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            value={busqueda}
            onChange={handleBusquedaChange}
            placeholder="Buscar por nombre, RUT o ubicación..."
            className="w-full pl-12 pr-10 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white"
          />
          {busqueda && (
            <button
              onClick={() => { setBusqueda(''); setCurrentPage(1); }}
              className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
              title="Limpiar búsqueda"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Table Card — vista de tabla (solo desde tablet hacia arriba) */}
      <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100 hidden md:block">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b-2 border-gray-200 bg-gray-50">
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">#</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Nombre</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">RUT</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Ubicación</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Día Fact.</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Toma Contador</th>
                {user?.role === 'admin' && <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Acciones</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {currentItems.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 mb-4">
                        <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <p className="text-lg font-medium text-gray-600">
                        {terminoBusqueda ? 'Sin resultados para tu búsqueda' : 'No hay clientes registrados'}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        {terminoBusqueda ? 'Prueba con otro nombre, RUT o ubicación' : 'Comience agregando un nuevo cliente'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                currentItems.map((cliente, index) => (
                  <tr key={cliente.id} className="hover:bg-gradient-to-r hover:from-gray-50 hover:to-transparent transition-all duration-200">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 text-blue-700 font-semibold text-xs">
                        {indexOfFirstItem + index + 1}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-semibold text-gray-900">{cliente.nombre}</td>
                    <td className="px-6 py-4 font-mono text-gray-700">{cliente.rut}</td>
                    <td className="px-6 py-4 text-gray-700">{cliente.ubicacion}</td>
                    <td className="px-6 py-4 text-center">
                      {cliente.dia_facturacion !== null && cliente.dia_facturacion !== undefined ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          Día {cliente.dia_facturacion}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {cliente.toma_contador ? (
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          cliente.toma_contador === 'Presencial'
                            ? 'bg-green-100 text-green-700'
                            : cliente.toma_contador === 'Online'
                              ? 'bg-cyan-100 text-cyan-700'
                              : 'bg-purple-100 text-purple-700'
                        }`}>
                          {cliente.toma_contador}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    {user?.role === 'admin' && (
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => openModal(cliente)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-all duration-200 transform hover:scale-105"
                            title="Editar"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Editar
                          </button>
                          <button
                            onClick={() => handleDelete(cliente.id)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-all duration-200 transform hover:scale-105"
                            title="Eliminar"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Eliminar
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Vista de tarjetas — solo en móvil (evita el scroll horizontal de la tabla) */}
      <div className="md:hidden space-y-3">
        {currentItems.length === 0 ? (
          <div className="bg-white shadow-lg rounded-2xl border border-gray-100 px-6 py-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <p className="text-base font-medium text-gray-600">
              {terminoBusqueda ? 'Sin resultados para tu búsqueda' : 'No hay clientes registrados'}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {terminoBusqueda ? 'Prueba con otro nombre, RUT o ubicación' : 'Comience agregando un nuevo cliente'}
            </p>
          </div>
        ) : (
          currentItems.map((cliente, index) => (
            <div
              key={cliente.id}
              className="bg-white shadow-lg rounded-2xl border border-gray-100 p-4"
            >
              {/* Encabezado de la tarjeta: número + nombre */}
              <div className="flex items-start gap-3 mb-3">
                <span className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 text-blue-700 font-semibold text-xs">
                  {indexOfFirstItem + index + 1}
                </span>
                <h3 className="font-bold text-gray-900 leading-snug flex-1 break-words">
                  {cliente.nombre}
                </h3>
              </div>

              {/* Datos etiquetados */}
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-500 flex-shrink-0">RUT</dt>
                  <dd className="font-mono text-gray-800 text-right break-all">{cliente.rut || '—'}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-500 flex-shrink-0">Ubicación</dt>
                  <dd className="text-gray-800 text-right break-words">{cliente.ubicacion || '—'}</dd>
                </div>
                <div className="flex justify-between items-center gap-3">
                  <dt className="text-gray-500 flex-shrink-0">Día facturación</dt>
                  <dd className="text-right">
                    {cliente.dia_facturacion !== null && cliente.dia_facturacion !== undefined ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        Día {cliente.dia_facturacion}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </dd>
                </div>
                <div className="flex justify-between items-center gap-3">
                  <dt className="text-gray-500 flex-shrink-0">Toma contador</dt>
                  <dd className="text-right">
                    {cliente.toma_contador ? (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        cliente.toma_contador === 'Presencial'
                          ? 'bg-green-100 text-green-700'
                          : cliente.toma_contador === 'Online'
                            ? 'bg-cyan-100 text-cyan-700'
                            : 'bg-purple-100 text-purple-700'
                      }`}>
                        {cliente.toma_contador}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </dd>
                </div>
              </dl>

              {/* Acciones (solo admin) */}
              {user?.role === 'admin' && (
                <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => openModal(cliente)}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-all duration-200"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Editar
                  </button>
                  <button
                    onClick={() => handleDelete(cliente.id)}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-all duration-200"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Eliminar
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Pagination Controls */}
      {clientesFiltrados.length > itemsPerPage && (
        <div className="mt-6 flex flex-wrap justify-center items-center gap-2">
          <button
            onClick={handlePrevious}
            disabled={currentPage === 1}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Anterior
          </button>

          <div className="flex items-center gap-1">
            {(() => {
              const maxVisiblePages = 5;
              let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
              let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

              if (endPage - startPage + 1 < maxVisiblePages) {
                startPage = Math.max(1, endPage - maxVisiblePages + 1);
              }

              const pages = [];

              // Primera página si no está visible
              if (startPage > 1) {
                pages.push(
                  <button
                    key={1}
                    onClick={() => handlePageChange(1)}
                    className="w-10 h-10 flex items-center justify-center rounded-xl border-2 border-gray-300 bg-white hover:bg-gray-50 text-sm font-medium text-gray-700 transition-all duration-200"
                  >
                    1
                  </button>
                );
                if (startPage > 2) {
                  pages.push(<span key="ellipsis1" className="px-2 text-gray-400 font-medium">...</span>);
                }
              }

              // Páginas visibles
              for (let i = startPage; i <= endPage; i++) {
                pages.push(
                  <button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    className={`w-10 h-10 flex items-center justify-center rounded-xl text-sm font-medium transition-all duration-200 ${
                      currentPage === i
                        ? 'bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-lg transform scale-110'
                        : 'border-2 border-gray-300 bg-white hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    {i}
                  </button>
                );
              }

              // Última página si no está visible
              if (endPage < totalPages) {
                if (endPage < totalPages - 1) {
                  pages.push(<span key="ellipsis2" className="px-2 text-gray-400 font-medium">...</span>);
                }
                pages.push(
                  <button
                    key={totalPages}
                    onClick={() => handlePageChange(totalPages)}
                    className="w-10 h-10 flex items-center justify-center rounded-xl border-2 border-gray-300 bg-white hover:bg-gray-50 text-sm font-medium text-gray-700 transition-all duration-200"
                  >
                    {totalPages}
                  </button>
                );
              }

              return pages;
            })()}
          </div>

          <button
            onClick={handleNext}
            disabled={currentPage === totalPages}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          >
            Siguiente
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-gray-200 transform transition-all duration-300">
            {/* Header */}
            <div className="bg-gradient-to-r from-sky-500 to-blue-600 px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                  <svg className="w-6 h-6 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white">
                  {editId ? 'Editar Cliente' : 'Nuevo Cliente'}
                </h3>
              </div>
            </div>

            {/* Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nombre del Cliente <span className="text-red-500">*</span>
                </label>
                <input
                  name="nombre"
                  placeholder="Ej: Empresa ABC S.A."
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  RUT <span className="text-red-500">*</span>
                </label>
                <input
                  name="rut"
                  placeholder="Ej: 12.345.678-9"
                  value={formData.rut}
                  onChange={handleChange}
                  required
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200 bg-white font-mono ${
                    formData.rut && !validarRut(formData.rut)
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                      : 'border-gray-200 focus:border-blue-500 focus:ring-blue-200'
                  }`}
                />
                {formData.rut && !validarRut(formData.rut) && (
                  <p className="mt-1 text-xs text-red-600">El RUT no es válido. Revisa el número y el dígito verificador.</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Ubicación <span className="text-red-500">*</span>
                </label>
                <input
                  name="ubicacion"
                  placeholder="Ej: Santiago Centro, Oficina 402"
                  value={formData.ubicacion}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Día de Facturación
                </label>
                <input
                  name="dia_facturacion"
                  type="number"
                  placeholder="Ingrese día (0-31)"
                  value={formData.dia_facturacion}
                  onChange={handleChange}
                  min="0"
                  max="31"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white"
                />
                <p className="text-xs text-gray-500 mt-1">Opcional: Día del mes para facturación (0-31)</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Toma de Contador
                </label>
                <select
                  name="toma_contador"
                  value={formData.toma_contador}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white appearance-none cursor-pointer"
                >
                  <option value="">-- Seleccionar método --</option>
                  <option value="Presencial">🏢 Presencial</option>
                  <option value="Online">💻 Online</option>
                  <option value="Envia Cliente">📧 Envía Cliente</option>
                </select>
              </div>

              {/* Footer con botones */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-5 py-3 bg-white border-2 border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-all duration-200"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-5 py-3 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-medium rounded-xl hover:shadow-lg transition-all duration-200 transform hover:scale-105 active:scale-95 inline-flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {editId ? 'Actualizar' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
